import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Mail, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supportApi, ApiError, type SupportMessage } from "@/lib/api";
import { useAuth } from "@/stores/auth";
import { LoadingBlock } from "@/components/ui/spinner";

const STATUS_STYLES: Record<SupportMessage["status"], string> = {
  new: "bg-info/10 text-info",
  read: "bg-muted text-muted-foreground",
  resolved: "bg-success/10 text-success",
  escalated: "bg-warning/15 text-warning-foreground",
};

export default function SupportPage() {
  const token = useAuth((s) => s.session?.accessToken);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    supportApi
      .list(token)
      .then(setMessages)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Could not load messages"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [token]);

  const setStatus = async (id: string, status: SupportMessage["status"]) => {
    if (!token) return;
    try {
      const updated = await supportApi.updateStatus(id, status, token);
      setMessages((prev) => prev.map((m) => (m.id === id ? updated : m)));
      toast.success(`Marked ${status}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not update message");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <LifeBuoy className="h-6 w-6" /> Support inbox
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Messages submitted via the marketing site contact form.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={loading ? "animate-spin" : ""} />
          Refresh
        </Button>
      </div>

      {error ? <p className="mt-6 text-sm text-destructive">{error}</p> : null}

      <div className="mt-6 space-y-3">
        {loading && messages.length === 0 ? <LoadingBlock label="Loading messages…" /> : null}
        {messages.length === 0 && !loading && !error ? (
          <p className="text-sm text-muted-foreground">No messages yet.</p>
        ) : null}
        {(!loading || messages.length > 0) && messages.map((m) => (
          <div key={m.id} className="rounded-lg border bg-surface p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 font-medium text-foreground">
                  {m.full_name}
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${STATUS_STYLES[m.status]}`}>
                    {m.status}
                  </span>
                </div>
                <a href={`mailto:${m.email}`} className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground hover:underline">
                  <Mail className="h-3 w-3" /> {m.email}
                </a>
                {m.company ? <div className="text-xs text-muted-foreground">{m.company}</div> : null}
              </div>
              <div className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString()}</div>
            </div>
            <p className="mt-3 whitespace-pre-wrap text-sm text-foreground">{m.message}</p>
            <div className="mt-3 flex gap-2">
              {m.status !== "read" ? (
                <Button size="sm" variant="outline" onClick={() => setStatus(m.id, "read")}>
                  Mark read
                </Button>
              ) : null}
              {m.status !== "resolved" ? (
                <Button size="sm" variant="outline" onClick={() => setStatus(m.id, "resolved")}>
                  Mark resolved
                </Button>
              ) : null}
              {m.status !== "escalated" && m.status !== "resolved" ? (
                <Button size="sm" variant="outline" onClick={() => setStatus(m.id, "escalated")}>
                  Escalate to admin
                </Button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
